/**
 * Subscription Lifecycle Manager
 *
 * Handles subscription state transitions, grace periods, and
 * webhook event processing. Provides simulation capabilities for testing.
 */

import { reportError } from '@/lib/errors';
import type {
  WebhookEvent,
  StripeSubscription,
  StripeInvoice,
  StripeCheckoutSession,
} from './webhook-types';
import { createMockEvent } from './webhook-types';
import type { Subscription, SubscriptionStatus, SubscriptionTier } from '../subscription/types';
import { useSubscriptionStore } from '../state/subscription-store';

// ============================================================================
// Constants
// ============================================================================

export const GRACE_PERIOD_DAYS = 7;
export const MAX_RETRY_ATTEMPTS = 3;
export const RETRY_INTERVALS_HOURS = [24, 48, 72]; // Hours between retries

// ============================================================================
// Types
// ============================================================================

export interface LifecycleEvent {
  type: 'status_change' | 'payment_success' | 'payment_failed' | 'grace_period_warning' | 'subscription_ended';
  previousStatus?: SubscriptionStatus;
  newStatus: SubscriptionStatus;
  subscription: Subscription;
  message: string;
  timestamp: string;
}

export type LifecycleEventHandler = (event: LifecycleEvent) => void;

// ============================================================================
// Subscription Lifecycle Manager
// ============================================================================

class SubscriptionLifecycleManager {
  private eventHandlers: Set<LifecycleEventHandler> = new Set();
  private gracePeriodTimers: Map<string, ReturnType<typeof setTimeout>> = new Map();

  /**
   * Subscribe to lifecycle events
   */
  onLifecycleEvent(handler: LifecycleEventHandler): () => void {
    this.eventHandlers.add(handler);
    return () => this.eventHandlers.delete(handler);
  }

  /**
   * Emit a lifecycle event to all subscribers
   */
  private emit(event: LifecycleEvent): void {
    this.eventHandlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        reportError(error, { context: 'SubscriptionLifecycle.emit', level: 'warning' });
      }
    });
  }

  // ========================================================================
  // Webhook Event Handlers
  // ========================================================================

  /**
   * Handle checkout session completed
   * Called when a customer completes checkout for a new subscription
   */
  handleCheckoutComplete(session: StripeCheckoutSession): void {
    const store = useSubscriptionStore.getState();

    if (session.subscription) {
      // Subscription was created during checkout
      const subscription = this.createSubscriptionFromStripe({
        id: session.subscription,
        customer: session.customer || '',
        status: 'active',
        current_period_start: Math.floor(Date.now() / 1000),
        current_period_end: Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60,
        cancel_at_period_end: false,
        canceled_at: null,
        ended_at: null,
        trial_start: null,
        trial_end: null,
        items: { data: [] },
        latest_invoice: null,
        default_payment_method: null,
        metadata: session.metadata,
      });

      store.setSubscription(subscription);
      if (session.customer) {
        store.setStripeCustomerId(session.customer);
      }

      this.emit({
        type: 'status_change',
        newStatus: 'active',
        subscription,
        message: 'Subscription activated successfully',
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Handle invoice paid
   * Called when a recurring payment succeeds
   */
  handleInvoicePaid(invoice: StripeInvoice): void {
    const store = useSubscriptionStore.getState();
    const subscription = store.subscription;

    if (!subscription || invoice.subscription !== subscription.id) {
      return;
    }

    // Clear any grace period
    this.clearGracePeriod(subscription.id);

    // Update subscription status to active
    const updatedSubscription: Subscription = {
      ...subscription,
      status: 'active',
    };

    store.setSubscription(updatedSubscription);
    store.resetRetryCount();

    this.emit({
      type: 'payment_success',
      previousStatus: subscription.status,
      newStatus: 'active',
      subscription: updatedSubscription,
      message: 'Payment successful',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Handle invoice payment failed
   * Called when a payment attempt fails
   */
  handlePaymentFailed(invoice: StripeInvoice): void {
    const store = useSubscriptionStore.getState();
    const subscription = store.subscription;

    if (!subscription || invoice.subscription !== subscription.id) {
      return;
    }

    // Increment retry count
    store.incrementRetryCount();
    const retryCount = store.paymentRetryCount;

    // Set grace period if not already set
    if (!store.gracePeriodEnd) {
      const gracePeriodEnd = new Date();
      gracePeriodEnd.setDate(gracePeriodEnd.getDate() + GRACE_PERIOD_DAYS);
      store.setGracePeriodEnd(gracePeriodEnd.toISOString());

      // Start grace period expiry timer
      this.startGracePeriodTimer(subscription.id, gracePeriodEnd);
    }

    // Update subscription status
    const updatedSubscription: Subscription = {
      ...subscription,
      status: 'past_due',
    };

    store.setSubscription(updatedSubscription);

    this.emit({
      type: 'payment_failed',
      previousStatus: subscription.status,
      newStatus: 'past_due',
      subscription: updatedSubscription,
      message: `Payment failed (attempt ${retryCount}/${MAX_RETRY_ATTEMPTS})`,
      timestamp: new Date().toISOString(),
    });

    // Check if grace period should expire
    if (retryCount >= MAX_RETRY_ATTEMPTS) {
      this.emit({
        type: 'grace_period_warning',
        newStatus: 'past_due',
        subscription: updatedSubscription,
        message: 'Final payment attempt failed. Subscription will be canceled soon.',
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Handle subscription updated
   * Called when subscription details change (plan change, cancel scheduled, etc.)
   */
  handleSubscriptionUpdated(
    stripeSubscription: StripeSubscription,
    previousAttributes?: Partial<StripeSubscription>
  ): void {
    const store = useSubscriptionStore.getState();
    const currentSubscription = store.subscription;

    if (!currentSubscription || stripeSubscription.id !== currentSubscription.id) {
      return;
    }

    const updatedSubscription = this.createSubscriptionFromStripe(stripeSubscription);
    store.setSubscription(updatedSubscription);

    // Detect status changes
    if (previousAttributes?.status && previousAttributes.status !== stripeSubscription.status) {
      this.emit({
        type: 'status_change',
        previousStatus: this.mapStripeStatus(previousAttributes.status),
        newStatus: updatedSubscription.status,
        subscription: updatedSubscription,
        message: `Subscription status changed to ${updatedSubscription.status}`,
        timestamp: new Date().toISOString(),
      });
    }
  }

  /**
   * Handle subscription deleted
   * Called when a subscription is fully canceled
   */
  handleSubscriptionDeleted(stripeSubscription: StripeSubscription): void {
    const store = useSubscriptionStore.getState();
    const currentSubscription = store.subscription;

    if (!currentSubscription || stripeSubscription.id !== currentSubscription.id) {
      return;
    }

    // Clear grace period timer
    this.clearGracePeriod(stripeSubscription.id);

    const updatedSubscription: Subscription = {
      ...currentSubscription,
      status: 'canceled',
      canceledAt: new Date().toISOString(),
    };

    store.setSubscription(updatedSubscription);

    this.emit({
      type: 'subscription_ended',
      previousStatus: currentSubscription.status,
      newStatus: 'canceled',
      subscription: updatedSubscription,
      message: 'Subscription has been canceled',
      timestamp: new Date().toISOString(),
    });
  }

  // ========================================================================
  // Grace Period Management
  // ========================================================================

  /**
   * Start a grace period expiry timer
   */
  private startGracePeriodTimer(subscriptionId: string, expiryDate: Date): void {
    // Clear any existing timer
    this.clearGracePeriod(subscriptionId);

    const msUntilExpiry = expiryDate.getTime() - Date.now();
    if (msUntilExpiry <= 0) {
      this.handleGracePeriodExpired(subscriptionId);
      return;
    }

    const timer = setTimeout(() => {
      this.handleGracePeriodExpired(subscriptionId);
    }, msUntilExpiry);

    this.gracePeriodTimers.set(subscriptionId, timer);
  }

  /**
   * Clear a grace period timer
   */
  private clearGracePeriod(subscriptionId: string): void {
    const timer = this.gracePeriodTimers.get(subscriptionId);
    if (timer) {
      clearTimeout(timer);
      this.gracePeriodTimers.delete(subscriptionId);
    }

    const store = useSubscriptionStore.getState();
    store.setGracePeriodEnd(null);
    store.resetRetryCount();
  }

  /**
   * Handle grace period expiration
   */
  private handleGracePeriodExpired(subscriptionId: string): void {
    const store = useSubscriptionStore.getState();
    const subscription = store.subscription;

    if (!subscription || subscription.id !== subscriptionId) {
      return;
    }

    // Downgrade to free/cancel subscription
    const updatedSubscription: Subscription = {
      ...subscription,
      status: 'canceled',
      canceledAt: new Date().toISOString(),
    };

    store.setSubscription(updatedSubscription);
    store.setGracePeriodEnd(null);
    store.resetRetryCount();

    this.emit({
      type: 'subscription_ended',
      previousStatus: 'past_due',
      newStatus: 'canceled',
      subscription: updatedSubscription,
      message: 'Subscription canceled due to payment failure',
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Check if subscription is in grace period
   */
  isInGracePeriod(): boolean {
    const store = useSubscriptionStore.getState();
    const gracePeriodEnd = store.gracePeriodEnd;

    if (!gracePeriodEnd) return false;
    return new Date(gracePeriodEnd) > new Date();
  }

  /**
   * Get days remaining in grace period
   */
  getGracePeriodDaysRemaining(): number {
    const store = useSubscriptionStore.getState();
    const gracePeriodEnd = store.gracePeriodEnd;

    if (!gracePeriodEnd) return 0;

    const endDate = new Date(gracePeriodEnd);
    const now = new Date();
    const diffMs = endDate.getTime() - now.getTime();

    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  // ========================================================================
  // Simulation Methods (for testing)
  // ========================================================================

  /**
   * Simulate a successful payment
   */
  simulatePaymentSuccess(): void {
    const store = useSubscriptionStore.getState();
    const subscription = store.subscription;

    if (!subscription) return;

    this.handleInvoicePaid({
      id: `inv_mock_${Date.now()}`,
      customer: subscription.stripeCustomerId || '',
      subscription: subscription.id,
      status: 'paid',
      amount_due: 2900,
      amount_paid: 2900,
      amount_remaining: 0,
      currency: 'chf',
      created: Math.floor(Date.now() / 1000),
      due_date: null,
      paid: true,
      payment_intent: null,
      hosted_invoice_url: null,
      invoice_pdf: null,
      attempt_count: 1,
      next_payment_attempt: null,
    });
  }

  /**
   * Simulate a failed payment
   */
  simulatePaymentFailure(): void {
    const store = useSubscriptionStore.getState();
    const subscription = store.subscription;

    if (!subscription) return;

    this.handlePaymentFailed({
      id: `inv_mock_${Date.now()}`,
      customer: subscription.stripeCustomerId || '',
      subscription: subscription.id,
      status: 'open',
      amount_due: 2900,
      amount_paid: 0,
      amount_remaining: 2900,
      currency: 'chf',
      created: Math.floor(Date.now() / 1000),
      due_date: Math.floor(Date.now() / 1000) + 7 * 24 * 60 * 60,
      paid: false,
      payment_intent: null,
      hosted_invoice_url: null,
      invoice_pdf: null,
      attempt_count: store.paymentRetryCount + 1,
      next_payment_attempt: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
    });
  }

  /**
   * Simulate grace period expiration (for testing)
   */
  simulateGracePeriodExpiry(): void {
    const store = useSubscriptionStore.getState();
    const subscription = store.subscription;

    if (subscription) {
      this.handleGracePeriodExpired(subscription.id);
    }
  }

  // ========================================================================
  // Helpers
  // ========================================================================

  /**
   * Map Stripe status to our SubscriptionStatus
   */
  private mapStripeStatus(stripeStatus: StripeSubscription['status']): SubscriptionStatus {
    const statusMap: Record<StripeSubscription['status'], SubscriptionStatus> = {
      active: 'active',
      past_due: 'past_due',
      canceled: 'canceled',
      unpaid: 'past_due',
      trialing: 'trialing',
      incomplete: 'incomplete',
      incomplete_expired: 'canceled',
    };
    return statusMap[stripeStatus] || 'none';
  }

  /**
   * Create a Subscription object from Stripe subscription data
   */
  private createSubscriptionFromStripe(stripeSubscription: StripeSubscription): Subscription {
    const tierFromMetadata = stripeSubscription.metadata.tier as SubscriptionTier | undefined;

    return {
      id: stripeSubscription.id,
      tier: tierFromMetadata || 'starter',
      status: this.mapStripeStatus(stripeSubscription.status),
      billingInterval: stripeSubscription.items.data[0]?.price.recurring.interval || 'month',
      currentPeriodStart: new Date(stripeSubscription.current_period_start * 1000).toISOString(),
      currentPeriodEnd: new Date(stripeSubscription.current_period_end * 1000).toISOString(),
      cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
      canceledAt: stripeSubscription.canceled_at
        ? new Date(stripeSubscription.canceled_at * 1000).toISOString()
        : undefined,
      trialEnd: stripeSubscription.trial_end
        ? new Date(stripeSubscription.trial_end * 1000).toISOString()
        : undefined,
      stripeSubscriptionId: stripeSubscription.id,
      stripeCustomerId: stripeSubscription.customer,
      stripePriceId: stripeSubscription.items.data[0]?.price.id,
    };
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const subscriptionLifecycle = new SubscriptionLifecycleManager();
